import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
  Breadcrumbs,
  Link,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import { NavigateNext as NavigateNextIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getData } from "../../Webservice/Webservice";

const rubikfont = {
  fontFamily: "Rubik, sans-serif",
};

function FAQs() {
  const navigate = useNavigate();
  const [faqData, setFaqData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const handleAccordionChange = (panelId) => (event, isExpanded) => {
    setExpandedId(isExpanded ? panelId : null);
  };

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    setLoading(true);
    try {
      const res = await getData("faq/list");
      if (res && res.topic) {
        setFaqData(res.topic);
      }
    } catch (error) {
      console.error("Error fetching FAQs:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredFaqData = faqData.map(section => ({
    ...section,
    faqs: section.faqs.filter(faq =>
      faq.question?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(section => section.faqs.length > 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#C8A054' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "#ffffff", minHeight: "100vh", py: { xs: 4, md: 8 } }}>
      <Container maxWidth="lg">
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" sx={{ color: "#999" }} />}
          aria-label="breadcrumb"
          sx={{ mb: 6 }}
        >
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate("/")}
            sx={{ cursor: "pointer", fontSize: "14px", fontWeight: 500, color: "#999" }}
          >
            HOME
          </Link>
          <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#222" }}>FAQs</Typography>
        </Breadcrumbs>

        {/* Professional Header */}
        <Box sx={{ mb: 6, textAlign: "center", maxWidth: "800px", mx: "auto" }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 600,
              fontSize: { xs: "28px", md: "42px" },
              color: "#111",
              ...rubikfont,
              mb: 2
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Typography
            sx={{
              color: "#666",
              fontSize: "16px",
              ...rubikfont,
              mb: 4
            }}
          >
            Find quick answers to common questions about our products, shipping, and more.
          </Typography>

          {/* Search Bar */}
          <TextField
            fullWidth
            placeholder="Search for questions..."
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              maxWidth: "600px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "30px",
                bgcolor: "#f9f9f9",
                "& fieldset": { borderColor: "#eee" },
                "&:hover fieldset": { borderColor: "#C8A054" },
                "&.Mui-focused fieldset": { borderColor: "#C8A054" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#999" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* FAQ Sections */}
        <Box sx={{ maxWidth: "850px", mx: "auto" }}>
          {filteredFaqData.length > 0 ? (
            filteredFaqData.map((section, index) => (
              <Box key={section.id || index} sx={{ mb: 6 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "#111",
                    mb: 2.5,
                    pb: 1,
                    borderBottom: "1px solid #eee",
                    letterSpacing: "0.5px",
                    ...rubikfont
                  }}
                >
                  {section.name?.toUpperCase()}
                </Typography>

                {section.faqs.map((faq, i) => {
                  const panelId = faq._id || `${index}-${i}`;
                  return (
                    <Accordion
                      key={panelId}
                      disableGutters
                      elevation={0}
                      expanded={expandedId === panelId}
                      onChange={handleAccordionChange(panelId)}
                      sx={{
                        border: "none",
                        borderBottom: "1px solid #f0f0f0",
                        "&:before": { display: "none" },
                        p: 0,
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: "#C8A054" }} />}
                        sx={{
                          px: 0,
                          py: 1,
                          "& .MuiAccordionSummary-content": { m: 1 }
                        }}
                      >
                        <Typography sx={{ fontWeight: 500, color: "#222", fontSize: "16px", ...rubikfont }}>
                          {faq.question}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 0, pb: 3 }}>
                        <Typography sx={{ color: "#555", lineHeight: 1.8, fontSize: "15px", ...rubikfont }}>
                          {faq.answer}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            ))
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography sx={{ color: '#999', fontSize: '18px', ...rubikfont }}>
                No FAQs found matching "{searchTerm}"
              </Typography>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
}

export default FAQs;
